import app from '../../forum/app';
import Component, { ComponentAttrs } from '../../common/Component';
import SubtreeRetainer from '../../common/utils/SubtreeRetainer';
import Dropdown from '../../common/components/Dropdown';
import PostControls from '../utils/PostControls';
import listItems from '../../common/helpers/listItems';
import ItemList from '../../common/utils/ItemList';
import type PostModel from '../../common/models/Post';
import LoadingIndicator from '../../common/components/LoadingIndicator';
import type Mithril from 'mithril';
import ResponseItemsBtn from '../../common/components/ResponseItemsBtn';
import Button from '../../common/components/Button';
import Select from '../../common/components/Select';
import { translate } from '../utils/translate';
export interface IPostAttrs extends ComponentAttrs {
  post: PostModel;
}
translate.selectLanguageTag.show = false; //不出现的select的选择语言
translate.language.translateLocal = true;
translate.service.use('client.edge'); //设置机器翻译服务通道

/**
 * The `Post` component displays a single post. The basic post template just
 * includes a controls dropdown; subclasses must implement `content` and `attrs`
 * methods.
 */
export default abstract class Post<CustomAttrs extends IPostAttrs = IPostAttrs> extends Component<CustomAttrs> {
  /**
   * May be set by subclasses.
   */
  loading = false;

  /**
   * Ensures that the post will not be redrawn
   * unless new data comes in.
   */
  subtree!: SubtreeRetainer;
  postBodyDom: Element | null = null;
  contentKey: number = 0;
  selectVal: string | null = 'null';
  selectOptions = {
    null: app.translator.trans('dl-response.forum.warn.translate'),
    english: 'English',
    chinese_simplified: '简体中文',
    chinese_traditional: '繁體中文',
    // japanese: '日本語',
    korean: '한국인',
    deutsch: 'Deutsch',
    french: 'Français',
    italian: 'Italiano',
    portuguese: 'Português',
    vietnamese: 'Tiếng Việt',
    spanish: 'español',
  };
  oninit(vnode: Mithril.Vnode<CustomAttrs, this>) {
    super.oninit(vnode);

    this.loading = false;

    this.subtree = new SubtreeRetainer(
      () => this.loading,
      () => this.attrs.post.freshness,
      () => {
        const user = this.attrs.post.user();
        return user && user.freshness;
      }
    );
  }

  view(vnode: Mithril.Vnode<CustomAttrs, this>) {
    const attrs = this.elementAttrs();

    attrs.className = this.classes(attrs.className as string | undefined).join(' ');

    const controls = PostControls.controls(this.attrs.post, this).toArray();
    const footerItems = this.footerItems().toArray();

    return (
      <article {...attrs}>
        <div key={this.contentKey}>
          {this.loading ? <LoadingIndicator /> : this.content()}
          <aside className="Post-actions Post-actions-lin">
            <ul>
              <div className="Post-actions-left">{listItems(this.actionItems().toArray())}</div>
              <li className="Post-actions-right">
                {!!controls.length && (
                  <Dropdown
                    className="Post-controls"
                    buttonClassName="Button Button--icon Button--flat"
                    menuClassName="Dropdown-menu--right"
                    icon="fas fa-ellipsis-h"
                    onshow={() => this.$('.Post-controls').addClass('open')}
                    onhide={() => this.$('.Post-controls').removeClass('open')}
                    accessibleToggleLabel={app.translator.trans('core.forum.post_controls.toggle_dropdown_accessible_label')}
                  >
                    {controls}
                  </Dropdown>
                )}
                <Select value={this.selectVal} options={this.selectOptions} onchange={(e: any) => this.onTranslate(e)} />
              </li>
            </ul>
          </aside>
          <footer className="Post-footer">{footerItems.length ? <ul>{listItems(footerItems)}</ul> : null}</footer>
        </div>
      </article>
    );
  }

  onbeforeupdate(vnode: Mithril.VnodeDOM<CustomAttrs, this>) {
    super.onbeforeupdate(vnode);

    return this.subtree.needsRebuild();
  }

  onupdate(vnode: Mithril.VnodeDOM<CustomAttrs, this>) {
    super.onupdate(vnode);

    const $actions = this.$('.Post-actions');
    const $controls = this.$('.Post-controls');

    $actions.toggleClass('openWithin', $controls.hasClass('open'));
  }

  /**
   * Get attributes for the post element.
   */
  elementAttrs(): Record<string, unknown> {
    return {};
  }

  /**
   * Get the post's content.
   */
  content(): Mithril.Children {
    // TODO: [Flarum 2.0] return `null`
    return [];
  }

  /**
   * Get the post's classes.
   */
  classes(existing?: string): string[] {
    let classes = (existing || '').split(' ').concat(['Post']);

    const user = this.attrs.post.user();
    const discussion = this.attrs.post.discussion();

    if (this.loading) {
      classes.push('Post--loading');
    }

    if (user && user === app.session.user) {
      classes.push('Post--by-actor');
    }

    if (user && user === discussion.user()) {
      classes.push('Post--by-start-user');
    }

    return classes;
  }

  /**
   * Build an item list for the post's actions.
   */
  actionItems(): ItemList<Mithril.Children> {
    return new ItemList();
  }

  /**
   * Build an item list for the post's footer.
   */
  footerItems(): ItemList<Mithril.Children> {
    return new ItemList();
  }

  onTranslate(e: any) {
    if (this.selectVal === e) return; // 如果选择的值没有变化，则不执行翻译操作
    this.selectVal = e;

    // 还原翻译：递增 contentKey 使 Mithril 销毁翻译态 DOM 并从 VDOM 重建，
    // 同时恢复原始内容和事件处理器（如收藏按钮的 onclick）
    if (e === 'null') {
      this.postBodyDom = null; // 清空缓存的 DOM 元素
      this.selectOptions['null'] = app.translator.trans('dl-response.forum.warn.translate'); // 重置选择值
      this.contentKey++;
      this.subtree.invalidate();
      return;
    }

    // 翻译操作
    const postElement = this.element; // 获取当前组件的根 DOM 元素
    const postBodyElements = postElement.getElementsByClassName('Post-box'); // 在当前组件范围内查找

    if (this.postBodyDom === null) {
      this.postBodyDom = postBodyElements[0].cloneNode(true) as Element;
    } else {
      // 切换翻译语言时，先用 innerHTML 恢复原文再重新翻译
      postBodyElements[0].innerHTML = this.postBodyDom.innerHTML;
    }
    this.selectOptions['null'] = app.translator.trans('dl-response.forum.warn.cancel_translate');
    const originalQuerySelectorAll = document.querySelectorAll;
    document.querySelectorAll = function (selector) {
      const elements = originalQuerySelectorAll.call(this, selector);
      if (selector === 'iframe') {
        return Array.from(elements).filter((iframe) => !iframe.matches('#ah-web-sdk-iframe'));
      }
      return elements;
    };
    const documents = [postBodyElements[0]]; // 转换为数组
    translate.language.setLocal(translate.language.recognition(this.postBodyDom.innerHTML).languageName);
    translate.setDocuments(documents);
    translate.language.clearCacheLanguage();
    translate.changeLanguage(e);

    // const obj = {
    //   from: translate.language.recognition(this.postBodyDom.innerHTML).languageName,
    //   to: e,
    //   texts: [this.postBodyDom.innerHTML],
    // };
    // translate.request.translateText(obj, (data:any) => {
    //   if (data.text[0] !== postBodyElements[0].innerHTML) {
    //     translate.changeLanguage(e);
    //   }
    // });
  }
}
